import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  OnDestroy,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { from, of } from 'rxjs';
import { catchError, finalize, mergeMap, take, tap, toArray } from 'rxjs/operators';

import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { MessageService } from 'primeng/api';

import { TicketDetail } from '../../core/models/ticket.model';
import { TicketService } from '../../core/services/ticket.service';
import { AutomationScope } from '../../core/automations/ticket-automation.model';
import { TicketAutomationService } from '../../core/automations/ticket-automation.service';

/**
 * Quantos PATCH da sugestão correm em paralelo.
 *
 * Na manutenção em massa a seleção pode ter centenas de chamados; um `forkJoin` dispararia
 * todos de uma vez. Com `mergeMap` limitado as requests escoam em ondas e o backend não leva
 * uma rajada.
 */
const APPLY_CONCURRENCY = 5;

/**
 * Modal de confirmação das automações do chamado.
 *
 * Não decide nada: apenas exibe a sugestão que o `TicketAutomationService` enfileirou e, se o
 * usuário confirmar, aplica o `rule.apply` via `patchTicket` em cada chamado envolvido.
 *
 * O save que originou a sugestão **já foi commitado** — cancelar não desfaz nada, só recusa a
 * sugestão.
 *
 * `scope` existe porque a tela de listagem monta dois hosts ao mesmo tempo (o
 * `app-ticket-detail-modal`, escopo `ticket-detail`, e o host do lote, escopo `bulk`); sem ele
 * os dois renderizariam a mesma sugestão.
 */
@Component({
  selector: 'app-ticket-automation-confirm',
  standalone: true,
  imports: [ButtonModule, DialogModule],
  templateUrl: './ticket-automation-confirm.component.html',
  styleUrls: ['./ticket-automation-confirm.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TicketAutomationConfirmComponent implements OnDestroy {
  private readonly automation = inject(TicketAutomationService);
  private readonly ticketService = inject(TicketService);
  private readonly messageService = inject(MessageService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);

  readonly scope = input.required<AutomationScope>();

  /** Hosts de detalhe: só exibe a sugestão deste chamado. Irrelevante no escopo `bulk`. */
  readonly ticketId = input<string | null | undefined>(null);

  /** Emitido antes do PATCH, para o host descartar o eco do socket (`_pendingSocketSkips`). */
  readonly applyStarted = output<void>();

  /** Chamados atualizados, para o host fazer o merge no estado da tela. */
  readonly applied = output<TicketDetail[]>();

  readonly suggestion = computed(() => this.automation.pendingFor(this.scope(), this.ticketId()));

  readonly applying = signal(false);
  /** Progresso `x/N` — o lote pode demorar o suficiente para o usuário querer saber. */
  readonly appliedCount = signal(0);

  readonly visible = computed(() => !!this.suggestion());

  readonly isBatch = computed(() => (this.suggestion()?.tickets.length ?? 0) > 1);

  readonly total = computed(() => this.suggestion()?.tickets.length ?? 0);

  readonly message = computed(() => {
    const s = this.suggestion();
    if (!s) return '';
    const { message, batchMessage } = s.rule.prompt;
    if (this.isBatch() && batchMessage) {
      return batchMessage.replace('{count}', String(s.tickets.length));
    }
    return message;
  });

  readonly acceptLabel = computed(() => this.suggestion()?.rule.prompt.acceptLabel ?? 'Confirmar');
  readonly rejectLabel = computed(() => this.suggestion()?.rule.prompt.rejectLabel ?? 'Cancelar');
  readonly header = computed(
    () => this.suggestion()?.rule.prompt.header ?? 'Sugestão de alteração'
  );

  ngOnDestroy(): void {
    // Sem isto, sair da tela sem responder deixaria a sugestão órfã na fila.
    this.automation.discardFor(this.scope(), this.ticketId());
  }

  /**
   * Fechar pelo X ou ESC descarta a sugestão — inclusive com o apply em voo.
   *
   * Deliberadamente SEM o guard de `applying()` que o `onReject` tem: um modal que não fecha é
   * um modo de falha pior do que qualquer inconsistência que o guard evitaria. Não é um
   * desfazer — o PATCH já foi enviado e, se der certo, o merge ainda acontece pelo `applied`.
   */
  onVisibleChange(open: boolean): void {
    if (open) return;
    const s = this.suggestion();
    if (s) this.automation.resolve(s.key);
  }

  onReject(): void {
    const s = this.suggestion();
    if (!s || this.applying()) return;
    this.automation.resolve(s.key);
  }

  onAccept(): void {
    const s = this.suggestion();
    if (!s || this.applying()) return;

    this.applying.set(true);
    this.appliedCount.set(0);
    this.applyStarted.emit();

    const failures: string[] = [];

    from(s.tickets)
      .pipe(
        mergeMap(
          t =>
            this.ticketService.patchTicket(t.id, s.rule.apply).pipe(
              // OBRIGATÓRIO: os observables do HttpClient deste app NUNCA completam — o
              // `AuthInterceptor` reembrulha toda request num `new Observable` que só chama
              // `next()`/`error()`, sem `complete()` (ver src/app/auth/auth.interceptor.ts).
              //
              // Sem este `take(1)`: (1) o `mergeMap` nunca libera vaga, então num lote só as
              // 5 primeiras requests saem e o resto fica pendurado; e (2) o `toArray()` e o
              // `finalize()` abaixo nunca rodam, deixando o modal aberto com o botão em
              // loading eterno e sem saída.
              // Mesmo motivo do `take(1)` em sprint-board.component.ts:2320.
              take(1),
              // Erro em um chamado não pode derrubar os outros do lote.
              catchError(err => {
                console.error('[ticket-automation] falha ao aplicar sugestão', t.number, err);
                failures.push(t.number);
                return of(null);
              }),
              tap(() => this.appliedCount.update(n => n + 1))
            ),
          APPLY_CONCURRENCY
        ),
        toArray(),
        // Navegar para fora com o apply em voo destruiria o host: sem isto a cadeia seguiria
        // e chamaria `applied.emit()` num OutputEmitterRef já destruído.
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.applying.set(false);
          this.cdr.markForCheck();
        })
      )
      .subscribe(results => {
        const updated = results.filter((t): t is TicketDetail => !!t);

        if (updated.length) this.applied.emit(updated);

        if (failures.length) {
          this.messageService.add({
            severity: updated.length ? 'warn' : 'error',
            summary: updated.length ? 'Aplicado parcialmente' : 'Erro',
            detail: `${updated.length} de ${s.tickets.length} atualizado(s). Falhou: ${failures.join(', ')}`,
            life: 6000,
          });
        } else {
          this.messageService.add({
            severity: 'success',
            summary: 'Aplicado',
            detail:
              updated.length === 1
                ? 'Sugestão aplicada com sucesso'
                : `${updated.length} chamados atualizados`,
            life: 3000,
          });
        }

        this.automation.resolve(s.key);
      });
  }
}
